import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import UFOSegment from "@atlassian/wac-ufo-segment";

import Card, { CardProps } from "@/components/wpl/card/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

export const SupportSection = () => {
    const intl = useIntl();

    const cardList: CardProps[] = [
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Support.card.headerText.advisoryServices",
                defaultMessage: "Advisory Services"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Support.card.bodyText.advisoryServices",
                defaultMessage:
                    "Maximize your Atlassian investment and achieve your goals with strategic, technical, and practice guidance from Atlassian’s in-house experts."
            }),
            iconName: "lightning",
            iconSize: "small",
            clickableCardUrl: useLocalizedHref(
                "/enterprise/services/advisory-services"
            ),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Support.card.linkText.learnMore",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref(
                        "/enterprise/services/advisory-services"
                    ),
                    doesArrowAnimate: true,
                    type: "basic",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "supportSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Support.card.linkText.learnMore",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Support.card.headerText.enterprisePartners",
                defaultMessage: "Enterprise Partners"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Support.card.bodyText.enterprisePartners",
                defaultMessage:
                    "Consulting, hands-on technical implementation, and best practices to optimize your Atlassian portfolio and ensure your success."
            }),
            iconName: "partners",
            iconSize: "small",
            clickableCardUrl: useLocalizedHref(
                "/enterprise/enterprise-partners"
            ),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Support.card.linkText.learnMore",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/enterprise/enterprise-partners"),
                    doesArrowAnimate: true,
                    type: "basic",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "supportSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Support.card.linkText.learnMore",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Support.card.headerText.customerSupport",
                defaultMessage: "Customer Support"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Support.card.bodyText.customerSupport",
                defaultMessage:
                    "Get 24/7 support with 30-minute response times for critical issues included with Cloud Enterprise and Data Center subscriptions."
            }),
            iconName: "sparkle",
            iconSize: "small",
            clickableCardUrl: useLocalizedHref("/enterprise/support-services"),
            ctaProps: {
                primaryLinkProps: {
                    text: intl.formatMessage({
                        id: "EnterpriseLandingPage.Support.card.linkText.learnMore",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/enterprise/support-services"),
                    doesArrowAnimate: true,
                    type: "basic",
                    dataEventProps: {
                        "data-product-key": "enterprise",
                        "data-event-component": "linkButton",
                        "data-event-container": "supportSection",
                        "data-event": "clicked",
                        "data-label": intl.formatMessage({
                            id: "EnterpriseLandingPage.Support.card.linkText.learnMore",
                            defaultMessage: "Learn more"
                        }),
                        "data-label-english": "Learn more"
                    }
                }
            }
        }
    ];

    return (
        <UFOSegment name="enterprise-page-supportSection">
            <section css={SupportSectionContainerStyles}>
                <Row layout="12">
                    <h2>
                        {intl.formatMessage({
                            id: "EnterpriseLandingPage.Support.headerText",
                            defaultMessage:
                                "Support designed for every enterprise"
                        })}
                    </h2>
                </Row>
                <Row layout="4-4-4">
                    {cardList.map((CardProps, index) => (
                        <Card
                            key={index}
                            className={`support-section-card support-section-card-${index}`}
                            headerType="h4"
                            {...CardProps}
                        />
                    ))}
                </Row>
            </section>
        </UFOSegment>
    );
};

const SupportSectionContainerStyles = css({
    "background": "#F0F1F2",
    "paddingBlockEnd": "40px",
    //Antialiasing fix: https://stackoverflow.com/questions/54458982/css-clip-path-little-gap-between
    "bottom": "-1px",
    "h2": {
        textAlign: "center",
        fontWeight: "700"
    },

    ".support-section-card": {
        "borderRadius": "20px",
        "cursor": "pointer",
        "boxShadow": "none",
        "border": "1px solid #B7B9BE",
        "transition": "box-shadow 0.2s ease-in-out",
        ".text-section": {
            padding: "24px",
            height: "100%"
        },
        "h4": {
            fontWeight: "700"
        },
        ".contentBlock-body": {
            height: "100%"
        },
        ".contentBlock-linkFlex": {
            marginBlockEnd: "0",
            a: {
                fontSize: "1rem",
                lineHeight: "1.5rem"
            }
        }
    },
    ".support-section-card:hover": {
        boxShadow: "0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F"
    },
    ".support-section-card-0": {
        ".icon path": {
            fill: "#32CD32"
        }
    },
    ".support-section-card-1": {
        ".icon path": {
            fill: "#FFA900"
        }
    },
    ".support-section-card-2": {
        ".icon path": {
            fill: "#BF63F3"
        }
    }
});

export default SupportSection;
